import { Avatar, CircularProgress } from '@mui/material';
import { toLower, upperFirst } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, FormControl, FormSelect, Modal, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { pictos } from '../assets/img/pictos';
import Chemin from '../components/Chemin';
import ErrorPopup from '../components/ErrorPopup';
import Navigation from '../components/Navigation';
import { CardGridSkeleton } from '../components/skeletons';
import { useDelayedLoading } from '../hooks/useDelayedLoading';
import { URL } from '../redux/api';
import {
  demandesRendezVousRedux,
  maJourneeRedux,
  programmeSelectRedux,
} from '../redux/evenementsServices';
import { setProgrammesNavigation } from '../redux/evenementsSlice';
import { annulerRdv, contactsRedux, creerRdv, standsByEventRedux, validerRdv } from '../redux/profilService';
import textConnect from '../textConnect.json';
import { formatTimeTo24h } from './fonctions';

const MesRendezVous = () => {
  const monProfil = useSelector(state => state.profil.profil);
  const accessToken = useSelector(state => state.user.access);
  const evenementSelected = useSelector(state => state.evenements.evenementSelect);
  const evenements = useSelector(state => state.evenements.evenements);
  const breadCrumbs = useSelector(state => state.profil.chemin);
  const dispatch = useDispatch();
  const navigationPage = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const showLoading = useDelayedLoading(loading, 500);
  const [openError, setOpenError] = useState(false);
  const [msgError, setMsgError] = useState('');
  const [demandes, setDemandes] = useState([]);
  const [confirmedRdv, setConfirmedRdv] = useState([]);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [submittingRdv, setSubmittingRdv] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [eventId, setEventId] = useState('');
  const [stands, setStands] = useState([]);
  const [heureDebut, setHeureDebut] = useState('');
  const [sujetRdv, setSujetRdv] = useState('');
  const [descriptionRdv, setDescriptionRdv] = useState('');
  const [standRdv, setStandRdv] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [openingConfirmedRdvId, setOpeningConfirmedRdvId] = useState(null);
  const [cancelingConfirmedRdvId, setCancelingConfirmedRdvId] = useState(null);
  const [confirmCancelItem, setConfirmCancelItem] = useState(null);
  const breadcrumbs_temp = breadCrumbs.filter(item => item.nom === 'Accueil');
  breadcrumbs_temp.push({ url: location.pathname, nom: 'Mes rendez-vous' });

  const closeErrorHandle = () => setOpenError(false);

  const loadData = () => {
    if (!accessToken || !evenementSelected?.id) {
      return;
    }
    setLoading(true);
    Promise.all([
      demandesRendezVousRedux(accessToken, evenementSelected.id),
      maJourneeRedux(accessToken, evenementSelected.id),
    ])
      .then(([demandesResponse, agendaResponse]) => {
        setDemandes(demandesResponse.data || []);
        const maJournee = Array.isArray(agendaResponse.data) ? agendaResponse.data : [];
        const rdvConfirmes = maJournee.filter(item => item?.is_rdv === true);
        setConfirmedRdv(rdvConfirmes);
      })
      .catch(error => {
        if (!navigator.onLine) {
          setMsgError(textConnect.internet);
        } else if (error?.response) {
          setMsgError(textConnect.error);
        } else {
          setMsgError(textConnect.internet);
        }
        setOpenError(true);
      })
      .finally(() => setLoading(false));
  };

  const handleDemandeAction = (item, choix) => {
    const programmeId = item?.programme?.id;
    if (!programmeId || !monProfil?.id) return;
    setActionLoadingId(item.id);
    validerRdv(programmeId, monProfil.id, accessToken, choix)
      .then(() => {
        loadData();
      })
      .catch(error => {
        const msg = error?.response?.data?.message || 'Une erreur est survenue.';
        setMsgError(msg);
        setOpenError(true);
      })
      .finally(() => setActionLoadingId(null));
  };

  const handleCancelAction = item => {
    const programmeId = item?.programme?.id;
    if (!programmeId) return;
    setActionLoadingId(item.id);
    annulerRdv(programmeId, accessToken)
      .then(() => {
        loadData();
      })
      .catch(error => {
        const msg = error?.response?.data?.message || 'Une erreur est survenue.';
        setMsgError(msg);
        setOpenError(true);
      })
      .finally(() => setActionLoadingId(null));
  };

  const handleCancelConfirmedRdv = programmeId => {
    if (!programmeId) return;
    setCancelingConfirmedRdvId(programmeId);
    annulerRdv(programmeId, accessToken)
      .then(() => {
        loadData();
      })
      .catch(error => {
        const msg = error?.response?.data?.message || 'Une erreur est survenue.';
        setMsgError(msg);
        setOpenError(true);
      })
      .finally(() => setCancelingConfirmedRdvId(null));
  };

  const handleConfirmCancel = () => {
    if (!confirmCancelItem) return;
    if (confirmCancelItem.type === 'demande') {
      handleCancelAction(confirmCancelItem.item);
    } else {
      handleCancelConfirmedRdv(confirmCancelItem.id);
    }
    setConfirmCancelItem(null);
  };

  useEffect(() => {
    dispatch({
      type: 'Profil/addChemin',
      payload: breadcrumbs_temp,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.pathname]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, evenementSelected?.id]);

  const parseTimeParts = value => {
    const normalized = formatTimeTo24h(value);
    const [rawHour, rawMinute] = normalized.split(':');
    const hour = rawHour && /^\d+$/.test(rawHour) ? rawHour.padStart(2, '0') : '';
    const minute = rawMinute && /^\d+$/.test(rawMinute) ? rawMinute.padStart(2, '0') : '00';
    return { hour, minute };
  };

  const hourOptions = Array.from({ length: 24 }, (_, idx) => String(idx).padStart(2, '0'));
  const minuteOptions = Array.from({ length: 60 }, (_, idx) => String(idx).padStart(2, '0'));

  const eventOptions = useMemo(() => {
    const list = Array.isArray(evenements) ? [...evenements] : [];
    if (
      evenementSelected?.id &&
      !list.some(item => String(item.id) === String(evenementSelected.id))
    ) {
      list.unshift(evenementSelected);
    }
    return list;
  }, [evenementSelected, evenements]);

  const filteredContacts = useMemo(() => {
    const normalizedFilter = contactSearch.trim().toLowerCase();
    let list = contacts;
    if (normalizedFilter) {
      list = contacts.filter(item => {
        const firstName = (item?.prenom || '').toLowerCase();
        const lastName = (item?.user?.nom || '').toLowerCase();
        return `${firstName} ${lastName}`.includes(normalizedFilter);
      });
    }
    return [...list].sort((a, b) => {
      const nameA = `${a?.prenom || ''} ${a?.user?.nom || ''}`.toLowerCase();
      const nameB = `${b?.prenom || ''} ${b?.user?.nom || ''}`.toLowerCase();
      return nameA.localeCompare(nameB, 'fr');
    });
  }, [contactSearch, contacts]);

  const resetCreateForm = () => {
    setContactSearch('');
    setSelectedContactId('');
    setEventId(evenementSelected?.id ? String(evenementSelected.id) : '');
    setStands([]);
    setHeureDebut('');
    setSujetRdv('');
    setDescriptionRdv('');
    setStandRdv('');
  };

  const openCreateRdvModal = () => {
    resetCreateForm();
    setOpenCreateModal(true);
  };

  const closeCreateRdvModal = () => {
    setOpenCreateModal(false);
    resetCreateForm();
  };

  useEffect(() => {
    if (!openCreateModal || !accessToken) {
      return;
    }
    setContactsLoading(true);
    contactsRedux(accessToken)
      .then(response => {
        setContacts(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        setContacts([]);
      })
      .finally(() => setContactsLoading(false));
  }, [accessToken, openCreateModal]);

  useEffect(() => {
    if (!openCreateModal || !eventId || !accessToken) {
      setStands([]);
      setStandRdv('');
      return;
    }
    standsByEventRedux(accessToken, eventId)
      .then(response => {
        let standsData = Array.isArray(response?.data) ? response.data : [];
        // Add "Déjeuner Républicain" if not already present
        const hasRepublican = standsData.some(
          item => item.code?.toUpperCase() === 'DÉJEUNER RÉPUBLICAIN'
        );
        if (!hasRepublican) {
          standsData = [
            ...standsData,
            {
              id: 'dejeuner-republicain',
              code: 'DÉJEUNER RÉPUBLICAIN',
              description: 'DÉJEUNER RÉPUBLICAIN',
            },
          ];
        }
        // Sort alphabetically and uppercase all codes
        standsData = standsData.sort((a, b) => {
          const displayA = (a.description || a.code || '').toUpperCase();
          const displayB = (b.description || b.code || '').toUpperCase();
          return displayA.localeCompare(displayB, 'fr');
        });
        setStands(standsData);
      })
      .catch(() => {
        setStands([]);
      });
  }, [accessToken, eventId, openCreateModal]);

  const createRendezVous = () => {
    if (!selectedContactId || !eventId || !heureDebut || !sujetRdv || !standRdv) {
      return;
    }
    setSubmittingRdv(true);
    const selectedStand = stands.find(item => String(item.id) === String(standRdv));
    const standLabel = selectedStand?.description || selectedStand?.code || '';
    creerRdv(
      accessToken,
      eventId,
      heureDebut,
      '',
      sujetRdv,
      descriptionRdv,
      standRdv,
      selectedContactId,
      standLabel
    )
      .then(() => {
        closeCreateRdvModal();
        loadData();
      })
      .catch(error => {
        if (!navigator.onLine) {
          setMsgError(textConnect.internet);
        } else if (error?.response) {
          setMsgError(textConnect.error);
        } else {
          setMsgError(textConnect.internet);
        }
        setOpenError(true);
      })
      .finally(() => setSubmittingRdv(false));
  };

  const { pendingSent, pendingReceived } = useMemo(() => {
    const pending = demandes.filter(item => item?.status === 'EN_ATTENTE');
    return {
      pendingSent: pending.filter(item => item?.role === 'sent'),
      pendingReceived: pending.filter(item => item?.role === 'received'),
    };
  }, [demandes]);

  const formatSlot = programme => {
    if (!programme) return '';
    if (!programme.heure_fin) return formatTimeTo24h(programme.heure_deb);
    return `${formatTimeTo24h(programme.heure_deb)} - ${formatTimeTo24h(programme.heure_fin)}`;
  };

  const formatLocation = programme => {
    if (!programme) return '';
    if (programme?.salle) return upperFirst(toLower(programme.salle));
    if (programme?.stand?.code) return upperFirst(toLower(programme.stand.code));
    return '';
  };

  const goToMaJournee = () => {
    maJourneeRedux(accessToken, evenementSelected.id).then(response => {
      dispatch({
        type: 'evenements/addMajournee',
        payload: response.data,
      });
      navigationPage('/majournee');
      window.scrollTo(0, 0);
    });
  };

  const openConfirmedRdvDetail = programmeId => {
    if (!programmeId || !accessToken) return;
    const navigationIds = confirmedRdv.map(item => item?.id).filter(Boolean);
    const currentIndex = navigationIds.findIndex(id => String(id) === String(programmeId));
    dispatch(
      setProgrammesNavigation({
        ids: navigationIds,
        currentIndex,
      })
    );
    setOpeningConfirmedRdvId(programmeId);
    programmeSelectRedux(accessToken, programmeId)
      .then(response => {
        dispatch({
          type: 'evenements/addProgrammeSelect',
          payload: response.data,
        });
        const sentDemande = demandes.find(
          d => d.role === 'sent' && String(d.programme?.id) === String(programmeId)
        );
        const emeteurParam = sentDemande ? '?isEmeteur=true' : '';
        navigationPage(`/programme/${programmeId}/2${emeteurParam}`);
        window.scrollTo(0, 0);
      })
      .catch(error => {
        if (!navigator.onLine) {
          setMsgError(textConnect.internet);
        } else if (error?.response) {
          setMsgError(textConnect.error);
        } else {
          setMsgError(textConnect.internet);
        }
        setOpenError(true);
      })
      .finally(() => setOpeningConfirmedRdvId(null));
  };

  const rendezVousActionButtonStyle = {
    display: 'flex',
    flexWrap: 'wrap',
  };

  const renderDemandeList = (items, emptyMessage) => {
    if (items.length === 0) {
      return (
        <div
          style={{
            backgroundColor: '#fff',
            border: '1px dashed #c8d5ff',
            borderRadius: 10,
            padding: '10px 12px',
            color: '#134491',
            fontSize: 14,
          }}
        >
          {emptyMessage}
        </div>
      );
    }

    return items.map(item => {
      const programme = item?.programme || {};
      const contactUser =
        item?.role === 'sent' ? item?.destinateur?.user || {} : item?.emeteur?.user || {};
      const contactName = `${contactUser?.prenom || ''} ${contactUser?.nom || ''}`.trim();
      const isReceivedPending = item?.role === 'received' && item?.status === 'EN_ATTENTE';
      const isSentCancellable =
        item?.role === 'sent' && ['EN_ATTENTE', 'ACCEPTEE'].includes(item?.status);
      const isLoading = actionLoadingId === item.id;
      return (
        <div
          key={item.id}
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: 12,
            padding: 12,
            marginBottom: 10,
            backgroundColor: '#fff',
          }}
        >
          <div className="d-flex align-items-center gap-2 mb-1">
            <Avatar src={contactUser?.photo || ''} sx={{ width: 36, height: 36 }} />
            <div style={{ fontWeight: 600, color: '#134491' }}>{contactName || '-'}</div>
          </div>
          <div style={{ fontWeight: 600, color: '#134491' }}>
            {programme?.titre || 'Rendez-vous'}
          </div>
          <div style={{ color: '#134491', fontSize: 14 }}>{formatSlot(programme)}</div>
          <div style={{ color: '#134491', fontSize: 14 }}>
            {item?.role === 'sent' ? 'Destinataire' : 'Emetteur'}: {contactName || '-'}
          </div>
          {isReceivedPending && (
            <div className="d-flex gap-2 mt-2">
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="success"
                    onClick={() => handleDemandeAction(item, 'accepter')}
                  >
                    Accepter
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => handleDemandeAction(item, 'refuser')}
                  >
                    Refuser
                  </Button>
                </>
              )}
            </div>
          )}
          {isSentCancellable && (
            <div className="d-flex gap-2 mt-2">
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => setConfirmCancelItem({ type: 'demande', item })}
                >
                  Annuler
                </Button>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const renderStatusBlock = (title, items, emptyMessage) => (
    <div style={{ marginBottom: 14 }}>
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ marginBottom: 8 }}
      >
        <div style={{ color: '#134491', fontWeight: 700 }}>{title}</div>
        <div
          style={{
            minWidth: 28,
            textAlign: 'center',
            borderRadius: 999,
            padding: '2px 8px',
            backgroundColor: '#e6eeff',
            color: '#134491',
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {items.length}
        </div>
      </div>
      {renderDemandeList(items, emptyMessage)}
    </div>
  );

  return (
    <Container fluid>
      <Modal show={openCreateModal} onHide={closeCreateRdvModal}>
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ color: 'white', backgroundColor: '#134491' }}
        >
          <h4>Nouvelle demande de rendez-vous</h4>
        </Modal.Header>
        <Modal.Body>
          <FormSelect value={eventId} onChange={e => setEventId(e.target.value)} className="mb-2">
            <option value="">Choisir un événement</option>
            {eventOptions.map(item => (
              <option key={item.id} value={item.id}>
                {item.nom}
              </option>
            ))}
          </FormSelect>

          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 12,
              padding: 10,
              marginBottom: 8,
              backgroundColor: '#f8f9ff',
            }}
          >
            <div style={{ color: '#134491', fontWeight: 600, marginBottom: 8 }}>
              Choisir un destinataire
            </div>
            <FormControl
              className="border mb-2"
              type="text"
              placeholder="Rechercher un contact"
              value={contactSearch}
              onChange={e => setContactSearch(e.target.value)}
            />
            <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 8 }}>
              {contactsLoading ? (
                <div className="d-flex justify-content-center py-2">
                  <CircularProgress size={20} />
                </div>
              ) : filteredContacts.length === 0 ? (
                <div style={{ color: '#134491', fontSize: 14 }}>Aucun contact trouvé.</div>
              ) : (
                filteredContacts.map((item, index) => {
                  const fullName = `${item?.prenom || ''} ${item?.user?.nom || ''}`.trim();
                  const isSelected = String(selectedContactId) === String(item?.id);
                  const photoUrl = item?.photo || item?.photo_path;
                  const baseUrl = URL.replace(/\/api.*$/, '').replace(/\/$/, '');
                  const photoSrc = photoUrl?.startsWith('/') ? `${baseUrl}${photoUrl}` : photoUrl;
                  return (
                    <button
                      type="button"
                      key={`${item.id}-${index}`}
                      onClick={() => setSelectedContactId(item.id)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        border: isSelected ? '1px solid #134491' : '1px solid #d9d9d9',
                        borderRadius: 8,
                        padding: '8px 10px',
                        marginBottom: 6,
                        backgroundColor: isSelected ? '#e6eeff' : '#fff',
                        color: '#134491',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <Avatar src={photoSrc} sx={{ width: 36, height: 36 }} />
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ fontWeight: 600 }}>{fullName || '-'}</div>
                        {item?.fonction ? (
                          <div style={{ fontSize: 13 }}>{item.fonction}</div>
                        ) : null}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <FormControl
            className="border mb-2"
            onChange={e => setSujetRdv(e.target.value)}
            placeholder="Saisir le sujet du rendez-vous"
            value={sujetRdv}
          />
          <FormControl
            className="border mb-2"
            onChange={e => setDescriptionRdv(e.target.value)}
            placeholder="Saisir la description du rendez-vous"
            value={descriptionRdv}
          />
          <div className="mb-2">
            Début :
            <div className="d-flex align-items-center gap-2 mt-1">
              {(() => {
                const { hour, minute } = parseTimeParts(heureDebut);
                return (
                  <>
                    <FormSelect
                      className="border"
                      value={hour}
                      onChange={e => {
                        const nextHour = e.target.value;
                        if (nextHour === '') {
                          setHeureDebut('');
                          return;
                        }
                        const nextMinute = minute || '00';
                        setHeureDebut(`${nextHour}:${nextMinute}`);
                      }}
                    >
                      <option value="">HH</option>
                      {hourOptions.map(hourValue => (
                        <option key={hourValue} value={hourValue}>
                          {hourValue}
                        </option>
                      ))}
                    </FormSelect>
                    <span>:</span>
                    <FormSelect
                      className="border"
                      value={minute}
                      onChange={e => {
                        const nextMinute = e.target.value || '00';
                        if (!hour) {
                          setHeureDebut('');
                          return;
                        }
                        setHeureDebut(`${hour}:${nextMinute}`);
                      }}
                    >
                      {minuteOptions.map(minuteValue => (
                        <option key={minuteValue} value={minuteValue}>
                          {minuteValue}
                        </option>
                      ))}
                    </FormSelect>
                  </>
                );
              })()}
            </div>
          </div>
          <FormSelect
            onChange={e => setStandRdv(e.target.value)}
            disabled={eventId === ''}
            value={standRdv}
            className="mb-2"
          >
            <option value="">Choisir un stand</option>
            {stands.map(item => (
              <option key={item.id} value={item.id} title={item.description}>
                {(item.description || item.code || '').toUpperCase()}
              </option>
            ))}
          </FormSelect>

          <div className="d-flex justify-content-end mt-3">
            {submittingRdv ? (
              <CircularProgress size={24} />
            ) : (
              <Button
                className="btn-recup"
                disabled={
                  eventId === '' ||
                  heureDebut === '' ||
                  sujetRdv.trim() === '' ||
                  standRdv === '' ||
                  selectedContactId === ''
                }
                onClick={createRendezVous}
              >
                Valider
              </Button>
            )}
            <Button className="btn-recup2 mx-2" variant="secondary" onClick={closeCreateRdvModal}>
              Fermer
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Row>
        <Navigation
          button={true}
          titre={'Mes rendez-vous'}
          icone={pictos.mes_rendez_vous_rond_icone}
          evenement={evenementSelected.photo}
          photo={monProfil.photo_path}
          retour="dashboard"
          selected={3}
          open={'true'}
          menus={true}
        />
      </Row>
      <Row className="p-0">
        <Chemin breadCrumbs={breadcrumbs_temp} />
      </Row>
      <ErrorPopup message={msgError} open={openError} close={closeErrorHandle} />
      <Container style={{ maxWidth: 1100, marginTop: 20, marginBottom: 30 }}>
        {showLoading ? (
          <CardGridSkeleton columns={3} rows={2} />
        ) : (
          <Row xs={1} md={3} className="g-3">
            <Col>
              <div style={{ background: '#f4f7ff', borderRadius: 16, padding: 16, height: '100%' }}>
                <h5 style={{ color: '#134491' }}>Envoyer des demandes</h5>
                <p style={{ color: '#134491' }}>
                  Créer une nouvelle demande de rendez-vous depuis la liste des participants.
                </p>
                <div className="d-flex justify-content-end" style={{ marginTop: 10 }}>
                  <Button onClick={openCreateRdvModal} style={rendezVousActionButtonStyle}>
                    Nouvelle demande
                  </Button>
                </div>
              </div>
            </Col>

            <Col>
              <div style={{ background: '#f4f7ff', borderRadius: 16, padding: 16, height: '100%' }}>
                <h5 style={{ color: '#134491' }}>Suivre mes demandes</h5>
                <p style={{ color: '#134491', marginBottom: 12, fontSize: 14 }}>
                  Retrouvez rapidement l'état de vos demandes de rendez-vous.
                </p>
                {renderStatusBlock('Envoyées', pendingSent, 'Aucune demande envoyée en attente.')}
                {renderStatusBlock('Reçues', pendingReceived, 'Aucune demande reçue en attente.')}
              </div>
            </Col>

            <Col>
              <div style={{ background: '#f4f7ff', borderRadius: 16, padding: 16, height: '100%' }}>
                <h5 style={{ color: '#134491' }}>Agenda des RDV confirmés</h5>
                {confirmedRdv.length === 0 ? (
                  <p style={{ color: '#134491' }}>Aucun rendez-vous confirmé pour le moment.</p>
                ) : (
                  confirmedRdv.map(item => (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (openingConfirmedRdvId === item.id) return;
                        openConfirmedRdvDetail(item.id);
                      }}
                      onKeyDown={e => {
                        if (openingConfirmedRdvId === item.id) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openConfirmedRdvDetail(item.id);
                        }
                      }}
                      aria-disabled={openingConfirmedRdvId === item.id}
                      style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: 12,
                        padding: 12,
                        marginBottom: 10,
                        backgroundColor: '#fff',
                        cursor: openingConfirmedRdvId === item.id ? 'wait' : 'pointer',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              backgroundColor: '#ff6a00',
                              color: '#fff',
                              borderRadius: 2,
                              padding: '3px 8px',
                              fontWeight: 600,
                              marginRight: 8,
                              fontSize: 14,
                            }}
                          >
                            {formatSlot(item)}
                          </div>
                          {formatLocation(item) ? (
                            <div className="d-flex align-items-center" style={{ color: '#134491' }}>
                              <img
                                src={pictos.lieu_icone}
                                alt=""
                                style={{ width: 18, height: 18, marginRight: 4 }}
                              />
                              <span style={{ fontSize: 14 }}>{formatLocation(item)}</span>
                            </div>
                          ) : null}
                        </div>
                        {openingConfirmedRdvId === item.id ? (
                          <CircularProgress size={24} />
                        ) : (
                          <img
                            src={pictos.rendez_vous_min_icone}
                            alt=""
                            style={{ width: 42, height: 42 }}
                          />
                        )}
                      </div>
                      <div style={{ color: '#ff6a00', marginBottom: 4 }}>
                        {item?.theme ? upperFirst(toLower(item.theme)) : 'Rendez-vous'}
                      </div>
                      <div style={{ fontWeight: 700, color: '#134491' }}>
                        {item.titre || 'Rendez-vous'}
                      </div>
                      {item?.description ? (
                        <div style={{ color: '#134491', fontSize: 14, marginTop: 4 }}>
                          {item.description}
                        </div>
                      ) : null}
                      <div
                        className="d-flex justify-content-end"
                        style={{ marginTop: 8 }}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                      >
                        {cancelingConfirmedRdvId === item.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => setConfirmCancelItem({ type: 'confirmed', id: item.id })}
                          >
                            Annuler
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
                <div className="d-flex justify-content-end" style={{ marginTop: 10 }}>
                  <Button onClick={goToMaJournee} style={rendezVousActionButtonStyle}>
                    Voir ma journée
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>
      <Modal show={!!confirmCancelItem} onHide={() => setConfirmCancelItem(null)}>
        <Modal.Header closeVariant="white" closeButton style={{ color: 'white', backgroundColor: '#134491' }}>
          <h4>Confirmation</h4>
        </Modal.Header>
        <Modal.Body>
          Voulez-vous vraiment annuler ce rendez-vous ?
          <div className="d-flex justify-content-end mt-3">
            {(actionLoadingId || cancelingConfirmedRdvId) ? (
              <CircularProgress size={20} />
            ) : (
              <Button className="btn-recup" onClick={handleConfirmCancel}>Confirmer</Button>
            )}
            <Button className="btn-recup2 mx-2" variant="secondary" onClick={() => setConfirmCancelItem(null)}>Fermer</Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default MesRendezVous;
